import { useRouter } from 'next/router'
import { useEffect } from 'react'

import { useQueryGetItemById, useQueryGetAllItems } from '@flecto/core/store'
import { useTranslation } from '@flecto/translations'
import { isValidParameterID } from '@flecto/utils'

import { BreadCrumbItemCategories } from '../../components/BreadcrumbItemCategories/BreadcrumbItemCategories'
import { CarouselItemCategories } from '../../components/CarouselItemCategories/CarouselItemCategories'
import { CarouselItems } from '../../components/CarouselItems/CarouselItems'
import { Page } from '../../components/Page/Page'
import { SectionItemDetails } from '../../components/SectionItemDetails/SectionItemDetails'
import { useItemSlugDecode } from '../../hooks/useItemSlugDecode'
import { useStoreCategories } from '../../hooks/useStoreCategories'
import { useStoreDetails } from '../../hooks/useStoreDetails'
import { useStorePathBuilder } from '../../hooks/useStorePathBuilder'

export function PageProductDetails() {
  const { t } = useTranslation('store', { keyPrefix: 'store-page-product' })
  const store = useStoreDetails()
  const path = useStorePathBuilder()
  const router = useRouter()
  const categories = useStoreCategories()
  const itemIdDecoded = useItemSlugDecode(router.query.itemId as string)

  const {
    data: item,
    isLoading: isLoadingItem,
    isError,
  } = useQueryGetItemById({
    storeDomain: store?.domain,
    itemId: itemIdDecoded ?? '',
  })

  const { data: similarItems, isLoading: isLoadingSimilarItems } =
    useQueryGetAllItems({
      storeDomain: store?.domain,
      options: {
        q: '',
        category_ids: [item?.category_id ?? ''],
        limit: 20,
        offset: 0,
        enabled: isValidParameterID(item?.category_id),
      },
    })

  const category = categories?.find(
    (cat) => String(cat.id) === String(item?.category_id)
  )

  const schemaMarkup = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: item?.title,
    image: item?.main_photo,
    description: item?.description,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'EUR',
      price: item?.pricing_rules ? item?.pricing_rules[0]?.price_cents : 0,
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: item?.pricing_rules ? item?.pricing_rules[0]?.price_cents : 0,
        priceCurrency: 'EUR',
        unitCode: 'DAY',
      },
    },
  })

  useEffect(() => {
    if (isError) {
      router.push(path('/'))
    }
  }, [isError])

  return (
    <Page
      htmlTitle={t('html_title', { store: store?.name, item: item?.title })}
      htmlMetaDescription={t('html_meta_description', { item: item?.title })}
      htmlMetaOgTitle={t('html_meta_og_title', {
        item: item?.title,
        store: store?.name,
      })}
      htmlMetaOgDescription={t('html_meta_og_description', {
        item: item?.title,
      })}
      schemaMarkup={schemaMarkup}
      hasCannonical
    >
      <Page.Section>
        <BreadCrumbItemCategories
          isLoading={isLoadingItem}
          itemCategory={category}
          mb='md'
          ml={2}
        />
        <SectionItemDetails item={item} isLoading={isLoadingItem} />
      </Page.Section>
      {similarItems?.data.length && !isLoadingSimilarItems && (
        <Page.Section>
          <CarouselItems
            mt='xl'
            title={t('label_carousel_products', { category: category?.name })}
            linkLabel={t('label_carousel_products_link')}
            linkHref={path(
              `/categories/${category?.id}?category_ids=${category?.id}`
            )}
            items={similarItems.data}
            excludeItems={item?.id ? [item?.id] : undefined}
          />
        </Page.Section>
      )}
      <Page.Section>
        <CarouselItemCategories
          itemCategories={categories}
          mt='xl'
          mb='xl'
          title={t('label_carousel_categories')}
        />
      </Page.Section>
    </Page>
  )
}
